// SignalR core imports
import { HubConnectionBuilder, HubConnection, LogLevel, HttpTransportType } from '@microsoft/signalr'

// React core hooks
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'

// Project utilities
import { devError, devLog, devWarn } from '../feedback'

// Redux
import { useDispatch } from 'react-redux'

// Connection status enum
/**
 * Enum representing the connection status of the SignalR connection.
 */
export enum ConnectionStatus {
	Disconnected = 'Disconnected',
	Connecting = 'Connecting',
	Connected = 'Connected',
	Reconnecting = 'Reconnecting',
	Failed = 'Failed',
}

/**
 * Interface for options that can be passed to the `useSignalR` hook.
 */
interface UseSignalROptions {
	/**
	 * Whether the connection should start automatically. Defaults to true.
	 */
	autoStart?: boolean

	/**
	 * Whether to skip the negotiation step. Defaults to false.
	 */
	skipNegotiation?: boolean

	/**
	 * Transport type for the SignalR connection. Defaults to WebSockets.
	 */
	transport?: number

	/**
	 * Fallback URLs to try if the primary connection fails.
	 */
	fallbackUrls?: string[]

	/**
	 * Whether credentials should be included in the connection. Defaults to false.
	 */
	withCredentials?: boolean

	/**
	 * Number of retry attempts for the connection. Defaults to 3.
	 */
	retryAttempts?: number
}

/**
 * Custom React hook for managing a SignalR connection.
 *
 * @param hubUrl - The URL of the SignalR hub to connect to.
 * @param dependencies - Dependencies that trigger a reconnection when changed.
 * @param options - Configuration options for the SignalR connection.
 * @returns An object containing the connection instance, connection status, and methods to start/stop the connection.
 */
export const useSignalR = (
	hubUrl: string,
	dependencies: any[] = [],
	options: UseSignalROptions = {
		autoStart: true,
		withCredentials: false,
		retryAttempts: 3,
	},
) => {
	const dispatch = useDispatch()
	// State to hold the connection status
	const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(ConnectionStatus.Disconnected)
	// State to hold the HubConnection instance
	const [connection, setConnection] = useState<HubConnection | null>(null)

	// Ref to hold the current connection instance and avoid stale closures/re-renders
	const connectionRef = useRef<HubConnection | null>(null)
	// Track retry attempts
	const retryCountRef = useRef<number>(0)

	// Stabilize options to prevent infinite re-renders
	const stableOptions = useMemo(
		() => ({
			autoStart: options.autoStart ?? true,
			withCredentials: options.withCredentials ?? false,
			retryAttempts: options.retryAttempts ?? 3,
			fallbackUrls: options.fallbackUrls || [],
			skipNegotiation: options.skipNegotiation,
			transport: options.transport,
		}),
		[
			options.autoStart,
			options.withCredentials,
			options.retryAttempts,
			JSON.stringify(options.fallbackUrls), // Stringify array for stable comparison
			options.skipNegotiation,
			options.transport,
		],
	)

	/**
	 * Creates a new SignalR connection instance.
	 *
	 * @param urlToUse - The URL to use for the connection. Defaults to the provided hubUrl.
	 * @returns A new HubConnection instance.
	 */
	const newConnection = useCallback(
		(urlToUse: string = hubUrl) => {
			try {
				const builder = new HubConnectionBuilder()
					.withUrl(urlToUse, {
						// Try WebSockets first, then fall back to other transports
						transport:
							HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling,
						// Handle CORS properly
						withCredentials: stableOptions.withCredentials || false,
						accessTokenFactory: undefined,
					})
					.withAutomaticReconnect({
						nextRetryDelayInMilliseconds: retryContext => {
							// Exponential backoff with max 30 seconds
							return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000)
						},
					})
					.configureLogging(LogLevel.Warning)

				return builder.build()
			} catch (error) {
				devError('⚠️ SignalR New Connection Error: ', error)
				throw error
			}
		},
		[hubUrl, stableOptions.withCredentials],
	)

	/**
	 * Starts the SignalR connection.
	 *
	 * @returns A promise that resolves when the connection is successfully established.
	 */
	const startConnection = useCallback(async (): Promise<void> => {
		if (connectionRef.current && connectionRef.current.state === 'Connected') {
			devLog('SignalR already connected')
			return Promise.resolve()
		}

		if (connectionRef.current && connectionRef.current.state !== 'Disconnected') {
			devLog('SignalR connection in progress, waiting...')
			return Promise.resolve()
		}

		setConnectionStatus(ConnectionStatus.Connecting)

		const maxRetries = stableOptions.retryAttempts || 3
		const urlsToTry = [hubUrl, ...(stableOptions.fallbackUrls || [])]

		for (const urlToTry of urlsToTry) {
			for (let attempt = 0; attempt < maxRetries; attempt++) {
				try {
					// Clean up any existing connection
					if (connectionRef.current) {
						try {
							await connectionRef.current.stop()
						} catch (stopError) {
							// Ignore stop errors when starting fresh
							devError('⚠️ SignalR Stop Error during startConnection cleanup: ', stopError)
						}
					}

					const connectionToUse = newConnection(urlToTry)
					setConnection(connectionToUse)
					connectionRef.current = connectionToUse

					await connectionToUse.start()

					// Set up event handlers after successful connection
					connectionToUse.on('RecieveNotification', message => devLog('🔔 SignalR Notification:', message))
					connectionToUse.on('RecieveProgress', (progress: number) => {
						devLog('📊 SignalR Progress:', progress)
						// Dispatch progress update action here if needed
						dispatch({
							type: 'massApprovalProgress/updateProgress',
							payload: progress,
						})
					})

					// Get connection ID
					try {
						const id = await connectionToUse.invoke('GetConnectionId')
						devLog('SignalR Connection ID:', id)
					} catch (invokeError) {
						devLog('Could not get connection ID, but connection is active')
					}

					devLog(`SignalR Connected to ${urlToTry}! ✅`)
					setConnectionStatus(ConnectionStatus.Connected)
					retryCountRef.current = 0
					return Promise.resolve()
				} catch (err) {
					devWarn(`⚠️ SignalR Connection attempt ${attempt + 1} failed for ${urlToTry}:`, err)

					if (attempt < maxRetries - 1) {
						await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)))
					}
				}
			}
		}

		// All attempts failed
		devError('⚠️ SignalR Connection failed after all attempts')
		setConnectionStatus(ConnectionStatus.Failed)

		connectionRef.current = null
		setConnection(null)

		return Promise.reject(new Error('Failed to connect to SignalR hub after all retry attempts'))
	}, [
		hubUrl,
		stableOptions.retryAttempts,
		stableOptions.fallbackUrls,
		newConnection,
		dispatch, // Add dispatch to dependencies
	])

	/**
	 * Stops the SignalR connection.
	 *
	 * @returns A promise that resolves when the connection is successfully stopped.
	 */
	const stopConnection = useCallback(async () => {
		const connectionToStop = connectionRef.current || connection

		if (connectionToStop) {
			try {
				await connectionToStop.stop()
				devLog('SignalR Disconnected! ❌')
			} catch (err) {
				devError('⚠️ SignalR Disconnection Error: ', err)
			} finally {
				setConnectionStatus(ConnectionStatus.Disconnected)
				connectionRef.current = null
				setConnection(null)
			}
		}
	}, [connection])

	// Only create connection instance once when component mounts
	useEffect(() => {
		if (!connectionRef.current) {
			const newConnectionInstance = newConnection()
			setConnection(newConnectionInstance)
			connectionRef.current = newConnectionInstance
		}

		// Cleanup function to prevent memory leaks
		return () => {
			if (connectionRef.current) {
				connectionRef.current.stop().catch(() => {
					// Ignore cleanup errors
				})
				connectionRef.current = null
				setConnection(null)
			}
		}
	}, []) // Empty dependency array - only run once

	return {
		connection,
		connectionStatus,
		startConnection,
		stopConnection,
	}
}
